import {Component, NgZone, OnInit, ViewEncapsulation} from '@angular/core';
import {Capacitor} from '@capacitor/core';
import {ApiService} from '../../../../../shared/services/api.service';
import {EnrollService} from '../../../../../shared/services/enroll.service';
import {DevicesService, LangService, LoaderService} from '@satoris/ngx-satoris';
import {MrzNfcDataInterface, MRZType} from '../../../../../shared/models/enroll';
import {format} from 'date-fns';
import getCountryISO2 from 'country-iso-3-to-2';

declare const SatorisOcr: any;

@Component({
  selector: 'app-activation-step-scan-mrz-nfc',
  templateUrl: './activation-step-scan-mrz-nfc.component.html',
  styleUrls: ['./activation-step-scan-mrz-nfc.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ActivationStepScanMrzNfcComponent implements OnInit{

  regionName = new Intl.DisplayNames([this.lang.readLangString.toLowerCase()], {
    type: 'region'
  });
  validDatas: MrzNfcDataInterface;
  nfcFoundTimeout: NodeJS.Timeout;

  // Stored listener references so removeEventListener actually works
  private onNFCFound = () => this.zone.run(() => {
    this.loader.loading(true);
    clearTimeout(this.nfcFoundTimeout);
    this.nfcFoundTimeout = setTimeout(() => {
      this.loader.loading(false);
      this.stopNFC();
    }, 20000);
  });

  private onNFCReadSuccess = (datas: any) => this.zone.run(() => {
    clearTimeout(this.nfcFoundTimeout);
    let parsed: any;
    try {
      parsed = (this.devices.isDevices('cordova-ios') || this.devices.isDevices('ios'))
        ? JSON.parse(datas.detail.substring(0, datas.detail.length - 1).substring(1))
        : JSON.parse(datas.detail);
    } catch {
      this.onNFCError();
      return;
    }
    this.enroll.form.controls[this.enroll.fc.NFC].setValue(parsed);
    this.nfcDataProcessing();
  });

  private onNFCError = () => this.zone.run(() => {
    clearTimeout(this.nfcFoundTimeout);
    this.stopNFC();
    this.loader.loading(true, {type: 'error', message: this.lang.transform('error.nfc'), btnLabel: this.lang.transform('btn.retry')}).then((retry: boolean) => {
      if(retry) {
        this.startNFC();
      } else {
        this.enroll.navigate('previous');
      }
    });
  });

  constructor(public api: ApiService,
    public enroll: EnrollService,
    private devices: DevicesService,
    private loader: LoaderService,
    private lang: LangService,
    private zone: NgZone) {
    if(this.api.userInfo.nationalNumber && this.api.userRole.isCustomer) {
      this.api.setPrincipalMode = true;
    }
  }

  ngOnInit() {
    setTimeout(() => this.startNFC(), 500);
  }

  ngOnDestroy() {
    this.stopNFC();
    clearTimeout(this.nfcFoundTimeout);
    this.api.setPrincipalMode = false;
  }

  stopNFC(stopWatch = true) {
    if (stopWatch) {
      SatorisOcr.stopWatch();
    }
    document.removeEventListener('onNFCFound', this.onNFCFound);
    document.removeEventListener('onNFCReadSuccess', this.onNFCReadSuccess);
    document.removeEventListener('onNFCError', this.onNFCError);
    document.removeEventListener('onNFCReadError', this.onNFCError);
    this.validDatas = undefined;
  }

  startNFC(){
    // Remove any leftover listeners before adding fresh ones (safe on retry)
    this.stopNFC(false);
    SatorisOcr.startWatch(this.enroll.form.controls[this.enroll.fc.DOC_NUMBER].value,
      format(new Date(this.enroll.form.controls[this.enroll.fc.DATE_OF_BIRTH].value),
        Capacitor.getPlatform() !== 'ios' ? 'yyyy-MM-dd' : 'yyMMdd'),
      format(new Date(this.enroll.form.controls[this.enroll.fc.DOC_EXPIRY_DATE].value),
        Capacitor.getPlatform() !== 'ios' ? 'yyyy-MM-dd' : 'yyMMdd'),
      console.log, console.error);
    document.addEventListener('onNFCFound', this.onNFCFound);
    document.addEventListener('onNFCReadSuccess', this.onNFCReadSuccess);
    document.addEventListener('onNFCError', this.onNFCError);
    document.addEventListener('onNFCReadError', this.onNFCError);
  }

  nfcDataProcessing() {
    const nfcProcessing = (nfcDatas : any) =>{
      const newDatas = {...nfcDatas};
      if(newDatas.personal_number) {
        newDatas.personal_number = newDatas.personal_number.trim();
      }
      if(newDatas.primary_identifier && newDatas.secondary_identifier) {
        newDatas.global_name = newDatas.secondary_identifier + ' ' +newDatas.primary_identifier;
      }
      if(newDatas.issuing_authority) {
        newDatas.issuing_authority = this.regionName.of(getCountryISO2(newDatas.issuing_state));
      }
      if(newDatas.nationality) {
        newDatas.country = this.regionName.of(getCountryISO2(newDatas.nationality));
      }
      if( newDatas.date_of_issue) {
        newDatas.date_of_issue = newDatas.date_of_issue.substring(0, 4) + '-' + newDatas.date_of_issue.substring(4, 6) + '-' + newDatas.date_of_issue.substring(6, newDatas.date_of_issue.length);
      }
      if(newDatas.place_of_birth) {
        newDatas.place_of_birth = newDatas.place_of_birth.replaceAll('[', '').replaceAll(']', '').toLowerCase();
      }
      if(newDatas.issuing_authority) {
        newDatas.issuing_authority = newDatas.issuing_authority.toLowerCase().replaceAll('<', '').replaceAll('>', '');
      }
      if(newDatas.document_number) {
        newDatas.document_number = newDatas.document_number.toUpperCase().replaceAll('<', '').replaceAll('>', '');
      }
      if(newDatas.issuer) {
        newDatas.issuer = newDatas.issuer.substring(7, 25).toLowerCase();
      }
      return newDatas;
    };

    let formData: MrzNfcDataInterface = undefined;
    if(!this.enroll.form.controls[this.enroll.fc.MRZ].value) {
      formData = {
        document_number: this.enroll.form.controls[this.enroll.fc.DOC_NUMBER].value,
        birth_date: this.enroll.form.controls[this.enroll.fc.DATE_OF_BIRTH].value,
        expiry_date: this.enroll.form.controls[this.enroll.fc.DOC_EXPIRY_DATE].value
      };
    }

    if(this.enroll.MRZType === MRZType.TD1 || this.enroll.MRZType === MRZType.TD3){
      this.zone.run(() => {
        const newNFC = nfcProcessing(this.enroll.form.controls[this.enroll.fc.NFC].value);
        if(this.enroll.MRZType === MRZType.TD1 && newNFC.personal_number) {
          this.enroll.form.controls[this.enroll.fc.NNI].setValue(newNFC.personal_number);
        }
        this.validDatas = {...(formData ? formData : this.enroll.form.controls[this.enroll.fc.MRZ].value), ...newNFC};
      });
    } else {
      this.loader.loading(true, {type: 'error', message: this.lang.transform('global.error')});
    }
    this.loader.loading(false);
  }
}
